import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Bell, Lock, Monitor, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [alerts, setAlerts] = useState(user.email_alerts !== false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState([]);

  const loadSessions = () => api.get("/auth/sessions").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);

  const revokeOthers = async () => {
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(data.revoked > 0 ? `Signed out ${data.revoked} other session(s)` : "No other sessions");
      loadSessions();
    } catch {
      toast.error("Could not revoke sessions");
    }
  };

  const revokeOne = async (id) => {
    try {
      await api.delete(`/auth/sessions/${id}`);
      toast.success("Session revoked");
      loadSessions();
    } catch {
      toast.error("Could not revoke session");
    }
  };

  const toggleAlerts = async (v) => {
    setAlerts(v);
    try {
      await api.patch("/users/me", { email_alerts: v });
      await refresh();
      toast.success(v ? "Email alerts on" : "Email alerts off");
    } catch (e) {
      setAlerts(!v);
      toast.error("Could not update preference");
    }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) return toast.error("New password must be at least 8 characters");
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      await refresh();
      toast.success("Password updated");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your profile, notifications and security.</p>
      </div>

      {user.must_reset && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3" data-testid="must-reset-banner">
          You're using a temporary password. Please set a new password below.
        </div>
      )}

      <Card className="p-6 border-slate-200">
        <p className="font-display font-semibold text-slate-900 mb-1">Profile</p>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Name</Label><Input value={user.name} disabled className="mt-1.5 bg-slate-50" /></div>
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Email</Label><Input value={user.email} disabled className="mt-1.5 bg-slate-50" /></div>
        </div>
        <p className="text-[11px] text-slate-400 mt-2">Role: <span className="capitalize">{user.role}</span></p>
      </Card>

      <Card className="p-6 border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Bell size={18} className="text-slate-500 mt-0.5" />
            <div>
              <p className="font-display font-semibold text-slate-900">Email alerts</p>
              <p className="text-sm text-slate-500 mt-0.5">Get an email when a ticket you're involved in is created, updated or commented on.</p>
            </div>
          </div>
          <Switch checked={alerts} onCheckedChange={toggleAlerts} data-testid="email-alerts-switch" />
        </div>
      </Card>

      <Card className="p-6 border-slate-200">
        <p className="font-display font-semibold text-slate-900 mb-1 flex items-center gap-2"><Lock size={17} className="text-slate-500" /> Change password</p>
        <div className="space-y-3 mt-3">
          <div><Label className="text-xs uppercase tracking-wider text-slate-500">Current password</Label>
            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" data-testid="current-password-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">New password</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" data-testid="new-password-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-500">Confirm</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" data-testid="confirm-password-input" /></div>
          </div>
          <Button onClick={changePassword} disabled={saving} className="text-white" style={{ background: "var(--tenant-primary)" }} data-testid="change-password-btn">
            {saving ? "Saving…" : "Update password"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-slate-200" data-testid="sessions-card">
        <div className="flex items-center justify-between mb-1">
          <p className="font-display font-semibold text-slate-900 flex items-center gap-2"><Monitor size={17} className="text-slate-500" /> Active sessions</p>
          {sessions.length > 1 && (
            <Button variant="outline" size="sm" onClick={revokeOthers} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="revoke-others-btn">
              <LogOut size={14} className="mr-1.5" /> Sign out everywhere else
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-500 mb-3">Devices currently signed in to your account.</p>
        <div className="divide-y divide-slate-100 border border-slate-100 rounded-md">
          {sessions.map((s) => (
            <div key={s.id} className="flex items-center gap-3 px-4 py-3" data-testid="session-row">
              <Monitor size={18} className="text-slate-400 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-800 flex items-center gap-2">
                  {s.device_label}
                  {s.current && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">This device</span>}
                </p>
                <p className="text-[11px] text-slate-400">{s.ip || "unknown IP"} · active {timeAgo(s.last_used_at)}</p>
              </div>
              {!s.current && (
                <button onClick={() => revokeOne(s.id)} className="text-slate-300 hover:text-red-600" data-testid="revoke-session-btn" title="Revoke">
                  <LogOut size={16} />
                </button>
              )}
            </div>
          ))}
          {sessions.length === 0 && <p className="text-center text-sm text-slate-400 py-4">No active sessions.</p>}
        </div>
      </Card>
    </div>
  );
}
